/* eslint-disable @typescript-eslint/ban-ts-comment  */
import { DOCUMENT, Injectable, NgZone, inject } from '@angular/core';

import { EventService } from '@frontend/sports/common/core/utils/window-event';
import { WINDOW } from '@frontend/vanilla/core';
import { Observable, Subject, Subscription } from 'rxjs';

export type ScrollElement = Element | Window;

@Injectable({
    providedIn: 'root',
})
export class ScrollContainerService {
    private zone = inject(NgZone);

    target: ScrollElement | undefined;
    eventTarget: ScrollElement | undefined;
    private scrollSubject = new Subject<ScrollElement>();
    private scrollSubscription: Subscription | undefined;
    private readonly _window = inject(WINDOW);
    private readonly _doc = inject(DOCUMENT);

    constructor() {
        this.setTarget((this._doc.scrollingElement || this._window) as ScrollElement, this._window);
    }

    get scroll(): Observable<ScrollElement> {
        return this.scrollSubject.asObservable();
    }

    get scrollTop(): number {
        return (this.target as Window).scrollY || (this.target as Element).scrollTop || 0;
    }

    get height(): number {
        return (this.target as Window).innerHeight || (this.target as Element).clientHeight;
    }

    get container(): HTMLElement {
        //@ts-ignore allow dynamic accessor, please don't use this, instead, use typing.

        return this.target['document'] || this.target;
    }

    get scrollHeight(): number {
        return (this.target as Element).scrollHeight;
    }

    scrollTo(x?: number, y?: number): void {
        requestAnimationFrame(() => {
            const element = this.target as Element;

            if (x !== undefined) {
                element.scrollLeft = -x;
            }

            if (y !== undefined) {
                element.scrollTop = -y;
            }
        });
    }

    scrollToDelta(x?: number, y?: number): void {
        requestAnimationFrame(() => {
            const element = this.target as Element;

            if (x !== undefined) {
                element.scrollLeft = element.scrollLeft + x;
            }

            if (y !== undefined) {
                element.scrollTop = element.scrollTop + y;
            }
        });
    }

    /**
     * Set the element that will be scrolling.
     *
     * The method has two parameters because on mobile, at least, we can have the following situation:
     *   - when calling window.scrollTop the property is 0 even if the page is scrolled
     *   - if we use document.scrollingElement then the scrollTop property is correct but the scroll event does not fire on this element
     *
     * So the `target` element is the one that returns the correct `scrollTop` and the `scrollEventTarget` is the element on which the `scroll` event fires.
     *
     *                    :(
     *
     * @param target - element that returns the correct `scrollTop` value
     * @param [scrollEventTarget] - element on which the `scroll` event fires
     * @memberof ScrollContainerService
     */
    setTarget(target: ScrollElement, scrollEventTarget?: ScrollElement): void {
        if (this.scrollSubscription) {
            this.scrollSubscription.unsubscribe();
        }

        this.eventTarget = scrollEventTarget || target;
        const scrollService = new EventService(this.zone, this.eventTarget, 'scroll');

        this.target = target;
        this.scrollSubscription = scrollService.subscribe(() => {
            this.scrollSubject.next(target);
        });
    }
}
